import { useCallback, useEffect, useRef, useState } from 'react'
import { Checkbox, Form, Input, Modal } from 'semantic-ui-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { LoaderBar } from 'components'
import { ReasonPresetService } from 'utility'
import { useNotification } from 'hooks/toast'
import {
    answerNeedsReasonEditor,
    defaultReasonPresetSetting,
    formatGenerateSummary,
    getReasonPresetErrorMessage,
    normalizeReasonPresetSetting,
} from './utils'
import {
    ActionRow,
    AddRow,
    AnswerBlock,
    AnswerLabelRow,
    AnswerPill,
    Chip,
    ChipRow,
    Crumb,
    HeaderRow,
    Muted,
    QuestionCard,
    QuestionMeta,
    QuestionTitle,
    Screen,
    SourceBadge,
    TriggerTag,
} from './styles'

const sourceColors = source => {
    const key = String(source || '').toLowerCase()
    if (key === 'ai') return { bg: '#ccfbf1', color: '#0f766e' }
    if (key === 'manual') return { bg: '#e2e8f0', color: '#334155' }
    if (key === 'library') return { bg: '#ede9fe', color: '#6d28d9' }
    return { bg: '#ffedd5', color: '#c2410c' }
}

const answerKey = (question, answer) =>
    `${question.QuestionUId}:${answer.ResponseUId}`

const cloneReasons = reasons =>
    (Array.isArray(reasons) ? reasons : []).map(reason => ({ ...reason }))

export const ReasonPresetAdmin = ({
    templateId,
    templateName,
    resolving,
    onBack,
}) => {
    const { successToast, errorToast, warningToast } = useNotification()
    const errorToastRef = useRef(errorToast)
    errorToastRef.current = errorToast
    const [loading, setLoading] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [force, setForce] = useState(false)
    const [payload, setPayload] = useState(null)
    const [drafts, setDrafts] = useState({})
    const [draftText, setDraftText] = useState({})
    const [savingKey, setSavingKey] = useState('')
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [settings, setSettings] = useState(defaultReasonPresetSetting(templateId))
    const [savingSettings, setSavingSettings] = useState(false)

    const applyPayload = useCallback(next => {
        setPayload(next)
        const nextDrafts = {}
        const questions = next?.Questions || []
        questions.forEach(question => {
            const answers = question.Answers || []
            answers.forEach(answer => {
                nextDrafts[answerKey(question, answer)] = cloneReasons(
                    answer.Reasons,
                )
            })
        })
        setDrafts(nextDrafts)
        if (next?.Setting) {
            setSettings(
                normalizeReasonPresetSetting(next.Setting, templateId),
            )
        }
    }, [templateId])

    const load = useCallback(async () => {
        if (!templateId) return
        setLoading(true)
        try {
            const result = await ReasonPresetService.getByTemplate(templateId)
            applyPayload(result)
        } catch (error) {
            errorToastRef.current(getReasonPresetErrorMessage(error))
        } finally {
            setLoading(false)
        }
    }, [applyPayload, templateId])

    useEffect(() => {
        load()
    }, [load])

    const onGenerate = async () => {
        if (!templateId) return
        setGenerating(true)
        try {
            const result = await ReasonPresetService.generate({
                TemplateId: templateId,
                QuestionUId: null,
                Force: force,
            })
            const summary = formatGenerateSummary(result)
            if (Number(result?.Failed) > 0) warningToast(summary)
            else successToast(summary)
            await load()
        } catch (error) {
            errorToast(getReasonPresetErrorMessage(error))
        } finally {
            setGenerating(false)
        }
    }

    const updateDraft = (key, reasons) => {
        setDrafts(prev => ({ ...prev, [key]: reasons }))
    }

    const onAddReason = (key) => {
        const text = String(draftText[key] || '').trim()
        if (!text) return
        const current = drafts[key] || []
        if (current.length >= 30) {
            errorToast('An answer can have at most 30 reasons.')
            return
        }
        const exists = current.some(
            reason =>
                String(reason.ReasonText || '').trim().toLowerCase() ===
                text.toLowerCase(),
        )
        if (exists) {
            errorToast('That reason is already on this answer.')
            return
        }
        updateDraft(key, [
            ...current,
            {
                ReasonText: text,
                Source: 'Manual',
                IsActive: true,
                DisplayOrder: current.length + 1,
            },
        ])
        setDraftText(prev => ({ ...prev, [key]: '' }))
    }

    const onDropChip = (key, fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return
        const current = [...(drafts[key] || [])]
        const [moved] = current.splice(fromIndex, 1)
        current.splice(toIndex, 0, moved)
        updateDraft(key, current)
    }

    const onSaveAnswer = async (question, answer) => {
        const key = answerKey(question, answer)
        const reasons = drafts[key] || []
        setSavingKey(key)
        try {
            const result = await ReasonPresetService.saveReasons({
                TemplateId: templateId,
                QuestionUId: question.QuestionUId,
                TriggerResponseUId: answer.ResponseUId,
                Reasons: reasons.map((reason, index) => ({
                    ReasonText: reason.ReasonText,
                    DisplayOrder: index + 1,
                    IsActive: reason.IsActive !== false,
                })),
            })
            if (Array.isArray(result?.Reasons)) {
                updateDraft(key, cloneReasons(result.Reasons))
            }
            successToast('Reasons saved.')
            await load()
        } catch (error) {
            errorToast(getReasonPresetErrorMessage(error))
        } finally {
            setSavingKey('')
        }
    }

    const openSettings = async () => {
        setSettingsOpen(true)
        if (!templateId) return
        try {
            const result = await ReasonPresetService.getSetting(templateId)
            setSettings(
                normalizeReasonPresetSetting(result?.Setting, templateId),
            )
        } catch (error) {
            errorToast(getReasonPresetErrorMessage(error))
        }
    }

    const saveSettings = async () => {
        setSavingSettings(true)
        try {
            const language = String(settings.LanguageCode || '').trim()
            const result = await ReasonPresetService.saveSetting({
                TemplateId: templateId,
                IsEnabled: !!settings.IsEnabled,
                UseAI: !!settings.UseAI,
                RequireReason: !!settings.RequireReason,
                AllowOther: !!settings.AllowOther,
                MaxChips: Math.min(20, Math.max(1, Number(settings.MaxChips) || 8)),
                LanguageCode: language || null,
            })
            setSettings(
                normalizeReasonPresetSetting(result?.Setting, templateId),
            )
            successToast('Popup settings saved.')
            setSettingsOpen(false)
            await load()
        } catch (error) {
            errorToast(getReasonPresetErrorMessage(error))
        } finally {
            setSavingSettings(false)
        }
    }

    const questions = payload?.Questions || []

    return (
        <Screen>
            <Crumb>
                <button type="button" onClick={onBack}>
                    Templates
                </button>
                {templateName ? ` › ${templateName}` : ''} › Reason presets
            </Crumb>
            <HeaderRow>
                <div>
                    <h1>{templateName || 'Reason presets'}</h1>
                    <p>
                        {templateId
                            ? `Template ${templateId} · Reason presets`
                            : 'Template id is missing'}
                        {payload
                            ? ` · ${payload.QuestionCount ?? questions.length} questions · ${payload.ReasonCount ?? 0} reasons`
                            : ''}
                    </p>
                </div>
                <ActionRow>
                    <AppPrimaryButton
                        type="button"
                        loading={generating}
                        disabled={!templateId || loading}
                        onClick={onGenerate}
                    >
                        Generate reason presets
                    </AppPrimaryButton>
                    <AppSecondaryButton
                        type="button"
                        disabled={!templateId}
                        onClick={openSettings}
                    >
                        Settings
                    </AppSecondaryButton>
                    <AppSecondaryButton
                        type="button"
                        disabled={!templateId || loading}
                        onClick={load}
                    >
                        Reload list
                    </AppSecondaryButton>
                </ActionRow>
            </HeaderRow>
            <div style={{ marginBottom: 14 }}>
                <Checkbox
                    label="Regenerate existing AI and library reasons. Manual reasons are kept."
                    checked={force}
                    onChange={(_, data) => setForce(!!data.checked)}
                />
            </div>
            {(loading || resolving || generating) && <LoaderBar />}
            {!resolving && !templateId && (
                <Muted>
                    Open this page from the template menu so the template id
                    can be sent to the API.
                </Muted>
            )}
            {!loading && templateId && questions.length === 0 && (
                <Muted>
                    No reason presets yet. Generate them after the template
                    is saved.
                </Muted>
            )}
            {questions.map(question => (
                <QuestionCard key={question.QuestionUId || question.TemplateQuestionId}>
                    <QuestionTitle>{question.QuestionText}</QuestionTitle>
                    <QuestionMeta>
                        QuestionUId {question.QuestionUId}
                    </QuestionMeta>
                    {(question.Answers || []).map(answer => {
                        const key = answerKey(question, answer)
                        const reasons = drafts[key] || []
                        const editable = answerNeedsReasonEditor(answer)
                        return (
                            <AnswerBlock key={answer.ResponseUId}>
                                <AnswerLabelRow>
                                    <AnswerPill $trigger={!!answer.IsNegative}>
                                        {answer.AnswerText}
                                    </AnswerPill>
                                    {answer.IsNegative && (
                                        <TriggerTag>TRIGGER</TriggerTag>
                                    )}
                                </AnswerLabelRow>
                                {!editable ? (
                                    <Muted>No popup for this answer</Muted>
                                ) : (
                                    <>
                                        <ChipRow>
                                            {reasons.map((reason, index) => {
                                                const colors = sourceColors(
                                                    reason.Source,
                                                )
                                                return (
                                                    <Chip
                                                        key={`${reason.Id || reason.ReasonText}-${index}`}
                                                        draggable
                                                        onDragStart={event => {
                                                            event.dataTransfer.setData(
                                                                'text/plain',
                                                                String(index),
                                                            )
                                                        }}
                                                        onDragOver={event =>
                                                            event.preventDefault()
                                                        }
                                                        onDrop={event => {
                                                            event.preventDefault()
                                                            const from = Number(
                                                                event.dataTransfer.getData(
                                                                    'text/plain',
                                                                ),
                                                            )
                                                            onDropChip(
                                                                key,
                                                                from,
                                                                index,
                                                            )
                                                        }}
                                                    >
                                                        {reason.ReasonText}
                                                        {reason.Source && (
                                                            <SourceBadge
                                                                $bg={colors.bg}
                                                                $color={colors.color}
                                                            >
                                                                {reason.Source}
                                                            </SourceBadge>
                                                        )}
                                                        <button
                                                            type="button"
                                                            aria-label="Remove reason"
                                                            onClick={() =>
                                                                updateDraft(
                                                                    key,
                                                                    reasons.filter(
                                                                        (_, itemIndex) =>
                                                                            itemIndex !==
                                                                            index,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    </Chip>
                                                )
                                            })}
                                        </ChipRow>
                                        <AddRow>
                                            <Input
                                                placeholder="Add a reason"
                                                value={draftText[key] || ''}
                                                onChange={(_, data) =>
                                                    setDraftText(prev => ({
                                                        ...prev,
                                                        [key]: data.value,
                                                    }))
                                                }
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault()
                                                        onAddReason(key)
                                                    }
                                                }}
                                                style={{ minWidth: 240 }}
                                            />
                                            <AppSecondaryButton
                                                type="button"
                                                onClick={() => onAddReason(key)}
                                            >
                                                Add
                                            </AppSecondaryButton>
                                            <AppPrimaryButton
                                                type="button"
                                                loading={savingKey === key}
                                                onClick={() =>
                                                    onSaveAnswer(question, answer)
                                                }
                                            >
                                                Save reasons
                                            </AppPrimaryButton>
                                        </AddRow>
                                    </>
                                )}
                            </AnswerBlock>
                        )
                    })}
                </QuestionCard>
            ))}

            <Modal
                open={settingsOpen}
                onClose={() => !savingSettings && setSettingsOpen(false)}
                size="small"
            >
                <Modal.Header>Reason popup settings</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <Checkbox
                                label="Enable the reason popup"
                                checked={!!settings.IsEnabled}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        IsEnabled: !!data.checked,
                                    }))
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Use AI when generating (off reuses the library only)"
                                checked={!!settings.UseAI}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        UseAI: !!data.checked,
                                    }))
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Auditor must pick at least one reason"
                                checked={!!settings.RequireReason}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        RequireReason: !!data.checked,
                                    }))
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Allow an other reason"
                                checked={!!settings.AllowOther}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        AllowOther: !!data.checked,
                                    }))
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Max reasons an auditor can pick (1–20)</label>
                            <Input
                                type="number"
                                min={1}
                                max={20}
                                value={settings.MaxChips}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        MaxChips: data.value,
                                    }))
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Language code (blank uses the question language)</label>
                            <Input
                                value={settings.LanguageCode || ''}
                                onChange={(_, data) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        LanguageCode: data.value,
                                    }))
                                }
                            />
                        </Form.Field>
                        {settings.ResolvedFrom && (
                            <Muted>Resolved from {settings.ResolvedFrom}</Muted>
                        )}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        disabled={savingSettings}
                        onClick={() => setSettingsOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        loading={savingSettings}
                        onClick={saveSettings}
                    >
                        Save settings
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </Screen>
    )
}
